import { styled } from '@mui/material/styles';
import format from 'date-fns/format';
import Popover from '@mui/material/Popover';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { MRT_RowData, MRT_SortingState, MRT_TableInstance } from 'material-react-table';
import {
  useGetAllServiceProviders,
  useGetContractByShippingService,
  useGetContractFlowByContractIdAndShippingServiceId,
} from 'src/use-cases/serviceProvider/useServiceProvider';
import clsx from 'clsx';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ContractDto } from 'src/generated/services/TFinancialApi';
import { UserContext } from 'src/use-cases/user/userDetails/context';
import IconDown from 'src/assets/images/icons/chevron-down.svg';
import IconEdit from 'src/assets/images/icons/edit-2.svg';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useTourContext } from 'src/presentations/guided-tours';
import { CreateValidityPeriodModal } from './CreateValidityPeriodModal';
import { UpdateValidityPeriodModal } from './UpdateValidityPeriodModal';
import Button from '../../atom/button';
import { RateCardChargesTableMain } from './rateCardCharges/TableMain';
import { SelectDropdown } from './filter/SelectDropdown';
import { GenericTabs } from '../../molecules/tabs/GenericTabs';
import { ITabOption } from '../../molecules/tabs/types';
import { TablePercentageDiscount } from './rateCardCharges/TablePercentageDiscount';

const StyledPopover = styled(Popover)({
  '& .MuiPopover-paper': {
    width: '290px',
    // border: '1px solid #DFDFDF',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    paddingTop: '1px',
  },
});

const StyledBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  color: '#9F9F9F',
  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#9F9F9F',
    fontSize: '0.75rem',
    top: '3px',
    '@media (min-width: 1720px)': {
      fontSize: '16px',
    },
  },
  '& .MuiFormControl-root .MuiSelect-select': {
    fontSize: '13px',
    '@media (min-width: 1720px)': {
      fontSize: '16px',
    },
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    borderRadius: '6px',
    borderColor: '#DFDFDF',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#DFDFDF',
  },
});

const chargeCategoryOptions = [
  {
    value: 'declarationCharges',
    label: 'Declaration',
  },
  {
    value: 'shipmentCharges',
    label: 'Shipment',
  },
  {
    value: 'weightCharges',
    label: 'Weight',
  },
  {
    value: 'containerCharges',
    label: 'Container',
  },
  {
    value: 'percentageCharges',
    label: 'Percentage',
  },
];

const PANEL_TAB = {
  CHARGE_DETAILS: 'chargeDetails',
  RATE_CARD_DISCOUNT: 'rateCardDiscount',
} as const;

export const ContractManagerRateCardCharges = (props) => {
  const { shippingServiceId, setShippingServiceId, contract, setContract, contractId, modality } = props;
  const { appTour } = useTourContext();
  const tour = appTour.registerTour('rate-card-charges');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(PANEL_TAB.CHARGE_DETAILS);
  const [chargeCategory, setChargeCategory] = useState('declarationCharges');
  const [contractToUpdate, setContractToUpdate] = useState<ContractDto>(null);
  const [openCreateValidityPeriodModal, setOpenCreateValidityPeriodModal] = useState(false);
  const [openUpdateValidityPeriodModal, setOpenUpdateValidityPeriodModal] = useState(false);
  const [validityMenuEl, setValidityMenuEl] = useState<HTMLButtonElement | null>(null);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [tableFilters, setTableFilters] = useState<Record<string, string[]>>({});
  const [tableSorting, setTableSorting] = useState<MRT_SortingState>([
    {
      desc: false,
      id: 'chargeName',
    },
  ]);

  const tableConfigRef = useRef<MRT_TableInstance<MRT_RowData> | null>(null);
  const { userDetails } = useContext(UserContext);

  const { data: contractList, status: getContractListStatus } = useGetContractByShippingService(shippingServiceId, {
    enabled: !!shippingServiceId,
  });

  const { data: contractFlow, isSuccess: contractFlowSuccess } = useGetContractFlowByContractIdAndShippingServiceId(contract?.id, shippingServiceId, {
    enabled: !!contract && !!shippingServiceId,
  });

  const { data: dataProviders, isLoading: isLoadingProviders } = useGetAllServiceProviders(modality);
  const selectedProvider = dataProviders?.data?.find((p) => p.id === contract?.serviceProviderId);
  const showRateCardDiscountTab = selectedProvider?.hasDiscount ?? false;
  const isCheckingDiscount =
    !!shippingServiceId &&
    (getContractListStatus === 'loading' || isLoadingProviders || (getContractListStatus === 'success' && (contractList?.data?.length ?? 0) > 0 && !contract));

  const panelTabOptions = useMemo<ITabOption[]>(() => {
    const tabs: ITabOption[] = [{ value: PANEL_TAB.CHARGE_DETAILS, label: 'Charge Details' }];
    if (showRateCardDiscountTab) {
      tabs.push({ value: PANEL_TAB.RATE_CARD_DISCOUNT, label: 'Rate Card Discount' });
    }
    return tabs;
  }, [showRateCardDiscountTab]);

  useEffect(() => {
    if (!showRateCardDiscountTab && activeTab === PANEL_TAB.RATE_CARD_DISCOUNT) {
      setActiveTab(PANEL_TAB.CHARGE_DETAILS);
    }
  }, [showRateCardDiscountTab, activeTab]);

  // Helper function to find the most recent contract based on validity period
  const findMostRecentContract = (contracts) => {
    if (!contracts || contracts.length === 0) return null;

    // Sort contracts by end date in descending order (most recent first)
    return [...contracts].sort((a, b) => {
      const dateA = new Date(a.endDate).getTime();
      const dateB = new Date(b.endDate).getTime();
      return dateB - dateA; // Descending order
    })[0];
  };

  // Track when data is successfully loaded
  useEffect(() => {
    if (contractFlowSuccess && contractFlow) {
      setDataInitialized(true);
    }
  }, [contractFlowSuccess, contractFlow]);

  // Set contract based on contractId from URL or default to the most recent validity period
  useEffect(() => {
    if (getContractListStatus === 'success' && contractList && contractList.data.length > 0) {
      if (contractId) {
        // Find the contract with matching ID
        const matchingContract = contractList.data.find((c) => c.id === contractId);
        if (matchingContract) {
          setContract(matchingContract);
        } else {
          // If no matching contract found, fallback to the most recent contract
          const mostRecentContract = findMostRecentContract(contractList.data);
          setContract(mostRecentContract);
        }
      } else {
        // No contractId provided, default to the most recent contract
        const mostRecentContract = findMostRecentContract(contractList.data);
        setContract(mostRecentContract);
      }
    }
  }, [contractList, getContractListStatus, setContract, shippingServiceId, contractId]);

  // Handle charge category from URL
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      // Try to find the category in the options
      const foundCategory = chargeCategoryOptions.find((item) => item.value.toLowerCase() === categoryParam.toLowerCase());
      if (foundCategory) {
        setChargeCategory(foundCategory.value);
      }
    }
  }, [searchParams]);

  // Reset filters when contract or charge category changes
  useEffect(() => {
    setTableFilters({});
    // Reset sorting to default when contract or charge category changes
    setTableSorting([
      {
        desc: false,
        id: 'chargeName',
      },
    ]);
  }, [contract?.id, chargeCategory]);

  const isAdmin = userDetails?.roles?.includes('ADMIN') ?? false;
  const isRateCardViewer = userDetails?.roles?.includes('RATE_CARD_VIEWER') ?? false;
  const hasPageAccess = isAdmin || isRateCardViewer;

  const openValidityMenu = !!validityMenuEl;

  // Update URL when charge category changes
  const handleChargeCategoryChange = (value: string) => {
    setChargeCategory(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('category', value);
    setSearchParams(newParams);
  };

  if (!hasPageAccess) {
    return (
      <div className='flex flex-col h-full items-center justify-center bg-white'>
        <h2 className='text-24 font-bold text-neutral-60 mb-8'>Access Denied</h2>
        <p className='text-16 text-neutral-50'>You don&apos;t have permission to access this page.</p>
      </div>
    );
  }

  return (
    <>
      <div className='h-full bg-white'>
        {isCheckingDiscount ? (
          <div className='h-full flex items-center justify-center'>
            <CircularProgress size={20} thickness={4} />
          </div>
        ) : null}
        <div className={`flex flex-col h-full${isCheckingDiscount ? ' hidden' : ''}`}>
          <div className='flex items-center py-12 border-b border-neutral-20 px-14 xl:px-20'>
            <div className='flex-1 flex items-center'>
              {showRateCardDiscountTab ? (
                <div id={tour.registerStep('rateCardDiscountTab', [], true)}>
                  <GenericTabs
                    options={panelTabOptions}
                    defaultSelected={activeTab}
                    onChange={setActiveTab}
                    className='!w-auto !min-w-[275px] xl:!min-w-[320px]'
                  />
                </div>
              ) : (
                <h3 className='text-14 xl:text-16 text-neutral-50 font-medium overflow-hidden'>CHARGES DETAILS</h3>
              )}
            </div>
            <div className='flex items-center gap-12'>
              {activeTab === PANEL_TAB.CHARGE_DETAILS && (
                <StyledBox>
                  <SelectDropdown
                    label='Charge Type'
                    options={chargeCategoryOptions}
                    value={chargeCategory}
                    setValue={handleChargeCategoryChange}
                    urlParamName='category'
                  />
                </StyledBox>
              )}
              {shippingServiceId && (
                <div id={tour.registerStep('validityPeriodSelection', [], true)} className='relative'>
                  <button
                    type='button'
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      setValidityMenuEl(event.currentTarget);
                    }}
                    className={clsx(
                      'relative flex flex-col justify-center h-40 w-[270px] xl:w-[290px] px-8 xl:px-12 rounded-8 border',
                      openValidityMenu ? 'border-primary' : 'border-[#DFDFDF]'
                    )}
                  >
                    <div className='flex items-center gap-12 w-full'>
                      {contractList?.data?.some((c) => c.id === contract?.id) ? (
                        <span className='text-12 xl:text-14'>
                          {contract ? `${format(new Date(contract.startDate), 'dd-MMM-yyyy')} to ${format(new Date(contract.endDate), 'dd-MMM-yyyy')}` : ''}
                        </span>
                      ) : (
                        <div className='text-12 xl:text-14 text-neutral-50 font-medium px-12 xl:px-16 py-8'>No contracts available</div>
                      )}
                      <div
                        className={clsx(
                          'ml-auto w-[20px] h-[20px] flex items-center justify-center transition-all duration-300',
                          openValidityMenu && 'rotate-180'
                        )}
                      >
                        <img src={IconDown} alt='' />
                      </div>
                    </div>
                    <div className='py-4 px-8 bg-white text-10 xl:text-12 text-[#9F9F9F] absolute left-8 -top-12'>Validity Period</div>
                  </button>
                  <StyledPopover
                    open={openValidityMenu}
                    anchorEl={validityMenuEl}
                    onClose={() => setValidityMenuEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <div className='w-full border border-[#dfdfdf] rounded-8 bg-white py-6 flex flex-col'>
                      {isAdmin && (
                        <button
                          type='button'
                          onClick={() => setOpenCreateValidityPeriodModal(true)}
                          className='px-12 xl:px-16 py-8 flex items-center gap-8 text-primary text-12 xl:text-14 font-medium'
                        >
                          <AddIcon />
                          Add new
                        </button>
                      )}
                      {contractList && contractList.data.length > 0 ? (
                        contractList.data.map((currentContract) => (
                          <div
                            key={currentContract.id}
                            className={clsx(
                              'flex items-center gap-8 xl:gap-10 hover:bg-[#F5F6FA] cursor-pointer',
                              contract?.id === currentContract?.id && 'bg-[#F5F6FA]'
                            )}
                          >
                            <button
                              type='button'
                              className='flex-1 pl-12 xl:pl-16 py-12 xl:py-16 flex items-center gap-8'
                              onClick={() => {
                                setContract(currentContract);
                                setValidityMenuEl(null);
                              }}
                            >
                              <span className='flex-1 text-12 xl:text-14 text-left'>{`${format(new Date(currentContract.startDate), 'dd-MMM-yyyy')} to ${format(
                                new Date(currentContract.endDate),
                                'dd-MMM-yyyy'
                              )}`}</span>
                              {contract?.id === currentContract?.id && (
                                <div className='text-[#606060] flex items-center justify-center'>
                                  <CheckIcon sx={{ fontSize: '18px' }} />
                                </div>
                              )}
                            </button>
                            {isAdmin && (
                              <div className='pr-12 xl:pr-16 py-12 xl:py-16'>
                                <button
                                  aria-label='Edit'
                                  type='button'
                                  className='w-[20px] h-[20px] flex items-center justify-center'
                                  onClick={() => {
                                    setContractToUpdate(currentContract);
                                    setOpenUpdateValidityPeriodModal(true);
                                  }}
                                >
                                  <img src={IconEdit} alt='' />
                                </button>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className='text-12 xl:text-14 text-neutral-50 font-medium px-12 xl:px-16 py-8'>No contracts available</div>
                      )}
                    </div>
                  </StyledPopover>
                </div>
              )}
              {contractFlow && isAdmin && activeTab === PANEL_TAB.CHARGE_DETAILS && chargeCategory && (
                <Button
                  variant='green_outline'
                  className='!rounded-[6px] !pr-12 !pl-12 xl:pl-16 font-bold shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                  onClick={() => {
                    if (tableConfigRef?.current) tableConfigRef.current.setCreatingRow(true);
                  }}
                >
                  <div className='text-14 xl:text-16 flex items-center gap-8 xl:gap-10'>
                    Add New Charge
                    <AddIcon />
                  </div>
                </Button>
              )}
              {contractFlow && isAdmin && activeTab === PANEL_TAB.RATE_CARD_DISCOUNT && (
                <Button
                  variant='green_outline'
                  className=' !rounded-[6px] !pr-12 !pl-12 xl:pl-16 font-bold shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                  onClick={() => {
                    if (tableConfigRef?.current) tableConfigRef.current.setCreatingRow(true);
                  }}
                >
                  <div className='text-14 xl:text-16 flex items-center gap-8 xl:gap-10'>
                    Add New Discount
                    <AddIcon />
                  </div>
                </Button>
              )}
              <button type='button' aria-label='Close' className='ml-auto text-neutral-60' onClick={() => setShippingServiceId(null)}>
                <CloseIcon />
              </button>
            </div>
          </div>
          <div className='flex-1 overflow-auto'>
            {activeTab === PANEL_TAB.CHARGE_DETAILS && shippingServiceId && chargeCategory && contract && (
              <div id={tour.registerStep('chargesTable', [], true)}>
                <RateCardChargesTableMain
                  key={`${chargeCategory}-${contract?.id}`}
                  chargeCategory={chargeCategory}
                  contractFlow={contractFlow?.data}
                  tableRef={tableConfigRef}
                  filters={tableFilters}
                  setFilters={setTableFilters}
                  sorting={tableSorting}
                  setSorting={setTableSorting}
                  isAdmin={isAdmin}
                />
              </div>
            )}
            {activeTab === PANEL_TAB.RATE_CARD_DISCOUNT && shippingServiceId && contractFlow?.data && (
              <TablePercentageDiscount key={`discount-${contractFlow.data.id}`} contractFlow={contractFlow.data} tableRef={tableConfigRef} isAdmin={isAdmin} />
            )}
          </div>
        </div>
      </div>
      {shippingServiceId && (
        <CreateValidityPeriodModal shippingServiceId={shippingServiceId} open={openCreateValidityPeriodModal} setOpen={setOpenCreateValidityPeriodModal} />
      )}

      {shippingServiceId && contractToUpdate && contractFlow?.data && (
        <UpdateValidityPeriodModal
          contractToUpdate={contractToUpdate}
          shippingServiceId={shippingServiceId}
          open={openUpdateValidityPeriodModal}
          setOpen={setOpenUpdateValidityPeriodModal}
          contractFlow={contractFlow.data}
        />
      )}
    </>
  );
};
